/**
 * 令牌保存策略。
 *
 * 约束来源：CONTRACT.md 第 22/19 行与需求文档第十节。
 * - 客服接入 token 与源 token 不得进入 URL、日志或前端长期缓存；
 * - 身份切换 / 退出必须清理连接、缓存、票据与未发送草稿。
 *
 * 因此这里只提供**内存**令牌容器，并且默认不带任何持久化实现：
 * 页面刷新即失效，需要时由调用方重新走 exchange。刻意不暴露 localStorage 版本，
 * 避免"顺手持久化"把长期凭据写进浏览器存储。
 */

export interface PrincipalSummary {
  id: string;
  name: string;
  orgId: string;
  role: 'admin' | 'agent' | 'customer' | 'supervisor';
  scopeId: string;
}

export interface SupportSession {
  expiresAt: string;
  principal: PrincipalSummary;
  token: string;
}

export class MemoryTokenStore {
  private session: null | SupportSession = null;

  private readonly listeners = new Set<() => void>();

  get current(): null | SupportSession {
    return this.session;
  }

  /** 是否持有尚未按本地时钟过期的会话（服务端仍是最终判定者）。 */
  get isUsable(): boolean {
    if (!this.session) return false;
    const expiresAt = Date.parse(this.session.expiresAt);
    if (Number.isNaN(expiresAt)) return true;
    return expiresAt > Date.now();
  }

  get token(): null | string {
    return this.session?.token ?? null;
  }

  set(session: SupportSession): void {
    this.session = session;
    this.emit();
  }

  clear(): void {
    if (this.session === null) return;
    this.session = null;
    this.emit();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const listener of [...this.listeners]) listener();
  }
}

/**
 * 草稿容器：按会话隔离，身份切换时整体清空。
 * 首版策略是"设备/实例独立、不静默互相覆盖"，因此这里不做跨端同步。
 */
export class DraftStore {
  private drafts = new Map<string, string>();

  clear(): void {
    this.drafts.clear();
  }

  get(conversationId: string): string {
    return this.drafts.get(conversationId) ?? '';
  }

  remove(conversationId: string): void {
    this.drafts.delete(conversationId);
  }

  set(conversationId: string, text: string): void {
    if (text === '') {
      this.drafts.delete(conversationId);
      return;
    }
    this.drafts.set(conversationId, text);
  }
}